import { ValueFormatterFunc } from "ag-grid-community";
import { AdditionalColumnConfigFieldsFragment } from "generated/graphql";
import {
    readAdditionalColumnValue,
    RowWithMetadata
} from "in_queue/utils/additionalColumnsMetadata";
import { startCase } from "lodash";
import {
    AgGridColumnDefs,
    cellTooltipValueGetter,
    currencyFormatter,
    dfaxFormatter,
    NiraColDef,
    NiraColGroupDef,
    percentValueFormatter,
    presenceFormatter,
    roundingFormatter,
    stringFormatter
} from "./agGridHelpers";

const FORMATTERS_BY_KEY: Record<
    string,
    { formatter: ValueFormatterFunc; numeric: boolean }
> = {
    string: { formatter: stringFormatter, numeric: false },
    currency: { formatter: currencyFormatter, numeric: true },
    dfax: { formatter: dfaxFormatter, numeric: true },
    percentage: { formatter: percentValueFormatter, numeric: true },
    rounding: { formatter: roundingFormatter, numeric: true },
    presence: { formatter: presenceFormatter, numeric: false }
};

const buildAdditionalColumnDef = <
    TData extends RowWithMetadata,
    TColumn extends string
>(
    config: AdditionalColumnConfigFieldsFragment
): NiraColDef<TData, TColumn> => {
    const formatOptions = FORMATTERS_BY_KEY[config.formatter];

    const isNumeric = Boolean(formatOptions?.numeric);

    return {
        colId: config.column_key as TColumn,
        headerName: config.display_name ?? startCase(config.column_key),
        wrapHeaderText: true,
        // A floor rather than a fixed width: the column starts at AG Grid's default
        // 200 px -- enough for long string values like PJM's "Cost Allocation
        // Criteria" -- and can be dragged no narrower than 120 px.
        minWidth: 120,
        valueGetter: ({ data }) =>
            data
                ? readAdditionalColumnValue(data.metadata, config.column_key)
                : null,
        valueFormatter: formatOptions?.formatter ?? stringFormatter,
        // Expose the full value on hover so long values stay readable when the cell
        // truncates. Uses the shared helper so the tooltip shows the *formatted*
        // value ("$1.23M", not 1234567.89), matching every other column in these
        // tables.
        tooltipValueGetter: cellTooltipValueGetter,
        type: isNumeric ? "numericColumn" : undefined
    };
};

const sortByColId = <TData, TColumn extends string>(
    a: NiraColDef<TData, TColumn>,
    b: NiraColDef<TData, TColumn>
): number => (a.colId ?? "").localeCompare(b.colId ?? "");

/**
    Takes in the static column defs from tables (ScenarioProjectsTable, ScenarioConstraintsTable, etc.)
    and the addition column configs from the backend and then inserts the addt'l column defs within the 
    static column defs based on the anchor_to_column and anchoring fields. 
    
    E.g:
    static column defs:
    [
        { colId: "projectId", headerName: "Project" },
        { colId: "numSharedUpgrades", headerName: "# shared upgrades" },
        { colId: "costPerMW", headerName: "$ / MW" },
        { colId: "sumAllocatedCost", headerName: "Allocated cost" },
        { colId: "erisSize", headerName: "Size (MW)" }
    ]
    
    additional column configs:
    [
        { column_key: "projectType", anchor_to_column: "projectId", anchoring: "after" },
        { column_key: "sumAllocatedCostWithoutSincs", anchor_to_column: "sumAllocatedCost", anchoring: "after" },
        { column_key: "nrisSize", anchor_to_column: "erisSize", anchoring: "before" },
        { column_key: "jtiq" }, // Gets appended to the end
    ]

    results:
    [
        { colId: "projectId", headerName: "Project" },
        { colId: "projectType", headerName: "Project Type" },
        { colId: "numSharedUpgrades", headerName: "# shared upgrades" },
        { colId: "costPerMW", headerName: "$ / MW" },
        { colId: "sumAllocatedCost", headerName: "Allocated cost" },
        { colId: "sumAllocatedCostWithoutSincs", headerName: "Allocated cost without SINC upgrades" },
        { colId: "nrisSize", headerName: "NRIS Size (MW)" },
        { colId: "erisSize", headerName: "Size (MW)" },
        { colId: "jtiq", headerName: "JTIQ" }
    ]
 */
export const spliceAdditionalColumns = <
    TData extends RowWithMetadata,
    TColumn extends string
>(
    columnDefs: AgGridColumnDefs<TData, TColumn>,
    configs: ReadonlyArray<AdditionalColumnConfigFieldsFragment>,
    anchorToColIdMap: Readonly<Record<string, TColumn>>
): AgGridColumnDefs<TData, TColumn> => {
    const before: Record<string, NiraColDef<TData, TColumn>[]> = {};
    const after: Record<string, NiraColDef<TData, TColumn>[]> = {};
    const append: NiraColDef<TData, TColumn>[] = [];

    for (const config of configs) {
        try {
            const colDef = buildAdditionalColumnDef<TData, TColumn>(config);
            const anchorColId = config.anchor_to_column
                ? anchorToColIdMap[config.anchor_to_column]
                : undefined;
            if (anchorColId && config.anchoring === "before") {
                (before[anchorColId] ||= []).push(colDef);
            } else if (anchorColId && config.anchoring === "after") {
                (after[anchorColId] ||= []).push(colDef);
            } else {
                append.push(colDef);
            }
        } catch (e) {
            console.error(e);
        }
    }

    // Sort the column defs when multiple are inserted at the same position to ensure deterministic ordering
    const result: AgGridColumnDefs<TData, TColumn> = [];
    for (const def of columnDefs) {
        try {
            const colId = (
                def as
                    | NiraColDef<TData, TColumn>
                    | NiraColGroupDef<TData, TColumn>
            ).colId;
            if (colId && before[colId]) {
                result.push(...before[colId].slice().sort(sortByColId));
            }
            result.push(def);
            if (colId && after[colId]) {
                result.push(...after[colId].slice().sort(sortByColId));
            }
        } catch (e) {
            console.error(e);
        }
    }
    result.push(...append.slice().sort(sortByColId));
    return result;
};
